import { Stack, Tooltip, Typography } from '@mui/material';
import { Control, FieldValues, Path, useWatch } from 'react-hook-form';

import { Select } from '@/components/Form/Select';
import { Switch } from '@/components/Form/Switch';
import { Icon } from '@/components/Icon/Icon';

import { getTenantConfigs } from '@/constants/tenant-configs';

import { useAppSelector } from '@/hooks/redux';
import { useTranslate } from '@/hooks/useTranslate';

import { selectLoggedInUser } from '@/redux/auth/selectors';

import { Option } from '@/types/components/common';

import { TaxResidencyTooltipContent } from './TaxResidencyTooltip';

type TaxResidencyFormFields = {
  differentTaxResidency: boolean;
  taxResidences?: string[];
};

export type TaxResidencySectionLabels = {
  toggleLabel: string;
  countryLabel: string;
  countryPlaceholder: string;
  countryRequiredError: string;
};

type TaxResidencySectionProps<T extends FieldValues & TaxResidencyFormFields> =
  {
    control: Control<T>;
    disabled?: boolean;
    popularCountryOptions: Option[];
    taxResidencesError?: boolean;
    taxResidencesHelperText?: string;
    labels?: Partial<TaxResidencySectionLabels>;
  };

export function TaxResidencySection<
  T extends FieldValues & TaxResidencyFormFields,
>({
  control,
  disabled = false,
  popularCountryOptions,
  taxResidencesError,
  taxResidencesHelperText,
  labels,
}: Readonly<TaxResidencySectionProps<T>>) {
  const { t } = useTranslate();
  const user = useAppSelector(selectLoggedInUser);
  const { maxTaxResidencySelection } = getTenantConfigs(user!.tenant);

  const differentTaxResidency = useWatch({
    control,
    name: 'differentTaxResidency' as Path<T>,
  });

  const toggleLabel =
    labels?.toggleLabel ?? t('createNewAccount.addressPage.form.tax.label');
  const countryLabel =
    labels?.countryLabel ??
    t('createNewAccount.addressPage.form.countryOfTax.label');
  const countryPlaceholder =
    labels?.countryPlaceholder ??
    t('createNewAccount.addressPage.form.countryOfTax.placeHolder');
  const countryRequiredError =
    labels?.countryRequiredError ??
    t('createNewAccount.addressPage.form.countryOfTax.error.required');

  return (
    <Stack direction="column" alignItems="stretch" gap={3}>
      <Switch
        control={control}
        name={'differentTaxResidency' as Path<T>}
        label={
          <Stack direction="row" alignItems="center" spacing={0.5}>
            <Typography variant="body2">{toggleLabel}</Typography>
            <Tooltip
              title={
                <TaxResidencyTooltipContent
                  maxSelections={maxTaxResidencySelection}
                />
              }
              placement="top"
              arrow
            >
              <span>
                <Icon
                  name="info-outline"
                  color={theme => theme.palette.grey[700]}
                />
              </span>
            </Tooltip>
          </Stack>
        }
      />
      {differentTaxResidency && (
        <Select
          options={popularCountryOptions}
          control={control}
          name={'taxResidences' as Path<T>}
          error={!!taxResidencesError}
          helperText={taxResidencesHelperText}
          rules={{
            required: countryRequiredError,
          }}
          fullWidth
          disabled={disabled}
          placeholder={countryPlaceholder}
          useDrawerOnMobile
          label={countryLabel}
          withSearchBar
          multiple
          maxSelections={maxTaxResidencySelection}
          className="multiSelectItem"
        />
      )}
    </Stack>
  );
}
